// src/services/homeService.ts
import { apiService } from './api';
import type { Conversation, DashboardGroupConversations } from '../types/dashboard';
import type { Layer } from '../types';

interface HomeAssetFile {
  FileId: number;
  FileName: string;
  FilePath: string;
  FileType: string;
  ConversationId: number;
  Layers: Layer[];
  ThumbnailUrl?: string;
  UploadedAt: string;
  UploadedBy: string;
}

// Types for GetFilesByPersonId API
export interface PersonFileThumbnail {
  ThumbnailId: number;
  FileName: string;
  FileExtension: string;
  Binary: string | null;
  FileUrl: string;
  TenantId: number;
  OldPath: string | null;
  Favorite: number;
}

export interface PersonFileLayer {
  ConversationLayerId: number | null;
  ConversationId: number;
  LayerId: number;
  LayerName: string;
  LayerColor: string;
  IsDefault: boolean;
  PriorityInConfigValue: number;
  WasEdited: any;
  IsActive: boolean;
}

export interface PersonFile {
  AttachmentId: number;
  EmbeddedImageId: number | null;
  Description: string;
  TempFile: string | null;
  FileName: string;
  FileContentType: string | null;
  FileSize: number;
  FileExtension: string;
  CreatedByID: number;
  UpatedByID: number;
  FileUrl: string;
  ThumbnailId: number;
  CreatedAt: string;
  UpdatedAt: string | null;
  Thumbnail: PersonFileThumbnail | null;
  CreatedByName: string;
  ConversationId: number;
  Title: string;
  Icon: string;
  Layers: PersonFileLayer[];
  TenantId: number;
  Favorite: number;
  IsImportant: boolean | null;
}

interface HomeDueConversationsResponse {
  Today: Conversation[];
  ThisWeek: Conversation[];
  NextWeek: Conversation[];
  Widgets: any[];
}

interface HomeMyCardsResponse {
  AssignedToMe: Conversation[];
  ApproverIsMe: Conversation[];
  TaggedMe: Conversation[];
  YourTeam: Conversation[];
}

interface DashboardFilter {
  FilterId: number;
  FieldName: string;
  Operator: string;
  Value: any;
}

interface HomeDashboard {
  DashboardId: number;
  Name: string;
  ConfigValueId: number;
  Filters: DashboardFilter[];
  DashboardGroups: DashboardGroupConversations[];
}

class HomeService
{
  /**
   * Obtiene las conversaciones del NewsFeed con filtros y búsqueda
   */
  async getHomeDueConversations(
    configValueId: number,
    searchText?: string,
    filters?: DashboardFilter[],
    pageNumber: number = 1,
    pageSize: number = 100,
  ): Promise<HomeDueConversationsResponse>
  {
    try
    {
      const params = {
        ConfigValueId: configValueId,
        SearchText: searchText || '',
        Filters: filters || [],
        PageNumber: pageNumber,
        PageSize: pageSize,
      };

      const response = await apiService.post<HomeDueConversationsResponse>(
        '/conversation/GetHomeDueConversations',
        params,
      );

      return response;
    }
    catch (error)
    {
      console.error('Error getting home due conversations:', error);
      // Retornar estructura vacía en caso de error
      return {
        Today: [],
        ThisWeek: [],
        NextWeek: [],
        Widgets: [],
      };
    }
  }

  /**
   * Obtiene las conversaciones por DashboardGroupId
   */
  async getConversationsByDashboardGroup(
    dashboardGroupId: number,
    configValueId: number,
    showCompleted: boolean = false,
    searchText?: string,
    filters?: DashboardFilter[],
    pageNumber: number = 1,
    pageSize: number = 100,
  ): Promise<Conversation[]>
  {
    try
    {
      const params = {
        DashboardGroupId: dashboardGroupId,
        ConfigValueId: configValueId,
        ShowCompleted: showCompleted,
        SearchText: searchText || '',
        Filters: filters || [],
        PageNumber: pageNumber,
        PageSize: pageSize,
      };

      const response = await apiService.post<Conversation[]>(
        '/conversation/GetConversationsByDashboardGroup',
        params,
      );

      return response;
    }
    catch (error)
    {
      console.error('Error getting conversations by dashboard group:', error);
      return [];
    }
  }

  /**
   * Obtiene los assets/archivos del usuario para la galería
   */
  async getHomeAssets(
    personId: number,
    pageNumber: number = 1,
    pageSize: number = 50,
  ): Promise<{ files: HomeAssetFile[]; hasMore: boolean }>
  {
    try
    {
      const params = {
        PersonId: personId,
        PageNumber: pageNumber,
        PageSize: pageSize,
      };

      const response = await apiService.post<{ Files: HomeAssetFile[]; HasMore: boolean }>(
        '/conversation/GetHomeAssets',
        params,
      );

      return {
        files: response.Files || [],
        hasMore: response.HasMore || false,
      };
    }
    catch (error)
    {
      console.error('Error getting home assets:', error);
      return {
        files: [],
        hasMore: false,
      };
    }
  }

  /**
   * Obtiene los archivos/imágenes del usuario por PersonId
   * GET api/conversation/GetFilesByPersonId
   */
  async getFilesByPersonId(params: {
    pageNumber: number;
    pageSize: number;
    personId: number;
    personIdVisitor: number;
  }): Promise<PersonFile[]>
  {
    try
    {
      const response = await apiService.get<PersonFile[]>(
        `/conversation/GetFilesByPersonId?pageNumber=${params.pageNumber}&pageSize=${params.pageSize}&personId=${params.personId}&personIdVisitor=${params.personIdVisitor}`,
      );
      return response || [];
    }
    catch (error)
    {
      console.error('Error getting files by person id:', error);
      return [];
    }
  }

  /**
   * Obtiene el dashboard de Home con sus grupos
   */
  async getHomeDashboard(configValueId: number): Promise<HomeDashboard | null>
  {
    try
    {
      const response = await apiService.get<HomeDashboard>(
        `/dashboard/GetHomeDashboard?configValueId=${configValueId}`,
      );

      return response;
    }
    catch (error)
    {
      console.error('Error getting home dashboard:', error);
      return null;
    }
  }

  /**
   * Obtiene todas las tarjetas "My Cards" organizadas por tipo
   */
  async getMyCards(
    configValueId: number,
    showCompleted: boolean = false,
    searchText?: string,
    filters?: DashboardFilter[],
  ): Promise<HomeMyCardsResponse>
  {
    try
    {
      // Obtener conversaciones de los 4 grupos diferentes en paralelo
      const [assignedToMe, approverIsMe, taggedMe, yourTeam] = await Promise.all([
        this.getConversationsByDashboardGroup(2, configValueId, showCompleted, searchText, filters), // Assigned to me
        this.getConversationsByDashboardGroup(3, configValueId, showCompleted, searchText, filters), // Approver is me
        this.getConversationsByDashboardGroup(4, configValueId, showCompleted, searchText, filters), // Tagged me
        this.getConversationsByDashboardGroup(5, configValueId, showCompleted, searchText, filters), // Your Team
      ]);

      return {
        AssignedToMe: assignedToMe,
        ApproverIsMe: approverIsMe,
        TaggedMe: taggedMe,
        YourTeam: yourTeam,
      };
    }
    catch (error)
    {
      console.error('Error getting my cards:', error);
      return {
        AssignedToMe: [],
        ApproverIsMe: [],
        TaggedMe: [],
        YourTeam: [],
      };
    }
  }

  /**
   * Busca conversaciones con autocomplete
   */
  async searchConversations(
    configValueId: number,
    searchText: string,
    maxResults: number = 10,
  ): Promise<string[]>
  {
    try
    {
      const response = await apiService.get<string[]>(
        `/conversation/SearchConversations?configValueId=${configValueId}&searchText=${encodeURIComponent(searchText)}&maxResults=${maxResults}`,
      );

      return response;
    }
    catch (error)
    {
      console.error('Error searching conversations:', error);
      return [];
    }
  }

  /**
   * Obtiene las conversaciones iniciales del dashboard
   */
  async getInitialDashboardGroupConversations(params: {
    CustomFields?: any[];
    DashboardId: number;
    DashboardViewTypeId: number;
    Filters: string;
    Me: string;
    PageNumber: number;
    PageSize: number;
    SearchText: string;
    IsHomeQuery: boolean;
    LayersToHide: string;
  }): Promise<DashboardGroupConversationsResponse[]>
  {
    try
    {
      const response = await apiService.post<DashboardGroupConversationsResponse[]>(
        '/conversation/GetInitialDashboardGroupConversations',
        params,
        { timeout: 120000 },
      );

      return response;
    }
    catch (error)
    {
      console.error('Error getting initial dashboard conversations:', error);
      return [];
    }
  }

  /**
   * NewsFeed del Home: endpoint dedicado (SP GetHomeNewsFeedConversations).
   * Mismo request y misma respuesta que GetInitialDashboardGroupConversations, pero solo
   * calcula las tarjetas de "Me" (Me = usuario cuyo home se muestra; la seguridad es la del usuario logueado).
   * POST api/conversation/GetHomeNewsFeed
   */
  async getHomeNewsFeed(params: {
    CustomFields?: any[];
    DashboardId: number;
    DashboardViewTypeId: number;
    Filters: string;
    Me: string;
    PageNumber: number;
    PageSize: number;
    SearchText: string;
    IsHomeQuery: boolean;
    LayersToHide: string;
  }): Promise<DashboardGroupConversationsResponse[]>
  {
    try
    {
      const response = await apiService.post<DashboardGroupConversationsResponse[]>(
        '/conversation/GetHomeNewsFeed',
        params,
        { timeout: 120000 },
      );

      return response;
    }
    catch (error)
    {
      console.error('Error getting home news feed:', error);
      return [];
    }
  }

  /**
   * Obtiene las conversaciones del Home (segunda columna - My Cards)
   * GET api/conversation/GetHomeConversations
   */
  async getHomeConversations(params: {
    PageNumber: number;
    ShowCompleted: number;
    PageSize: number;
    Me: string;
    LayersToHide: string;
  }): Promise<DashboardGroupConversationsResponse[]>
  {
    try
    {
      const response = await apiService.post<DashboardGroupConversationsResponse[]>(
        '/conversation/GetHomeConversations',
        params,
      );

      return response;
    }
    catch (error)
    {
      console.error('Error getting home conversations:', error);
      return [];
    }
  }

  /**
   * Obtiene los widgets del Home (tercera columna)
   * POST api/conversation/GetHomeConversationsWidget
   */
  async getHomeConversationsWidget(params: {
    PageNumber: number;
    ShowCompleted: number;
    PageSize: number;
    Me: string;
    LayersToHide: string;
  }): Promise<DashboardGroupConversationsResponse[]>
  {
    try
    {
      const response = await apiService.post<DashboardGroupConversationsResponse[]>(
        '/conversation/GetHomeConversationsWidget',
        params,
      );

      return response;
    }
    catch (error)
    {
      console.error('Error getting home conversations widget:', error);
      return [];
    }
  }

  /**
   * Obtiene las conversaciones Due del Home (tercera columna)
   * POST api/conversation/GetHomeDueConversations
   * Returns a flat list of ConversationDetail, each with Today=1/2/3 (Today/ThisWeek/NextWeek)
   */
  async getHomeDueConversationsNew(params: {
    PageNumber: number;
    ShowCompleted: number;
    PageSize: number;
    Me: string;
    LayersToHide: string;
  }): Promise<ConversationDetail[]>
  {
    try
    {
      const response = await apiService.post<ConversationDetail[]>(
        '/conversation/GetHomeDueConversations',
        params,
      );

      return Array.isArray(response) ? response : [];
    }
    catch (error)
    {
      console.error('Error getting home due conversations:', error);
      return [];
    }
  }
}

export const homeService = new HomeService();
export type {
  HomeAssetFile,
  HomeDueConversationsResponse,
  HomeMyCardsResponse,
  HomeDashboard,
  DashboardFilter,
};

// Tipos adicionales para GetInitialDashboardGroupConversations
export interface DashboardGroupConversationsResponse {
  DashboardGroupId: number;
  DashboardId: number;
  Name: string;
  Option?: string;
  Person?: string;
  sizeX: number;
  sizeY: number;
  row: number;
  col: number;
  IsDefault: boolean;
  showAllDetails: boolean;
  cancelDashboardNotification: boolean;
  UpdateGroup: any;
  Conversations: ConversationDetail[];
  SocketConnectionId: string | null;
}

export interface ConversationDetail {
  ConversationId: number;
  assignedPerson: any;
  assignedPersonId: any;
  MilestoneId: number;
  ProjectId: number;
  DefaultGroupId: any;
  parentDashboardGroups: any;
  ProjectName: string;
  ProjectBody: string | null;
  ProjectIsEnable: boolean;
  PersonId: number | null;
  CoreTaskId: number | null;
  CoreTaskName: string | null;
  Title: string;
  Body: string;
  IsRedFlag: boolean;
  IsFavorite: boolean;
  IsArchived: boolean;
  IsEnable: boolean;
  IsCustomType: boolean;
  IsProjectConversation: boolean;
  HasBeenEdited: boolean;
  DuplicateCards: any;
  CreatedByID: number;
  CreatedBy: string;
  UpdatedBy: string;
  CreatedAt: string;
  UpdatedAt: string;
  ConversationLayers: ConversationLayer[];
  ConversationGroups: any;
  ConversationSecurityGroups: any;
  LayerCustomFields: any[];
  CustomFields: any;
  CustomFieldValueImages: any[];
  Attachments: any[];
  EmbeddedImages: any;
  Replies: ConversationReply[];
  Conversations: any[];
  Tags: any;
  Hashtags: any;
  Phases: ConversationPhase[];
  LayersToHide: any;
  Icon: string;
  SideMenuText: string;
  ConfigValueId: number;
  Hide: boolean;
  IsParent: boolean;
  Progress: number;
  Finished: boolean;
  TotalReplies: number;
  TotalFavorites: number;
  IsFromProject: boolean;
  MiniatureDefaultImageName: string;
  RelatedConversationId: number;
  RelatedReplyId: number;
  ImageName: string | null;
  isUserFollowingConversation: number;
  IsAdmin: boolean;
  IsAdminProject: boolean;
  DashboardGroupId: number;
  DashboardGroupConversationId: number | null;
  DashboardId: number | null;
  TotalTasks: number;
  TenantId: number;
  ProjectConversationId: number;
  Metadata: any;
  LayersEdited: any;
  DefaultLayersDuplicateProject: any;
  SocketConnectionId: any;
  Recently: any;
  Today: any;
  OwnedById: number;
  OwnedBy: string | null;
  HasPhases: boolean;
  ConfigValueIdPhase: number | null;
  Order: number | null;
  Reactions: any[];
  IsImportant: boolean | null;
  ParentConversationId: number | null;
}

export interface ConversationLayer {
  ConversationLayerId: number | null;
  ConversationId: number;
  LayerId: number;
  LayerName: string;
  LayerColor: string;
  IsDefault: boolean;
  PriorityInConfigValue: number;
  WasEdited: any;
  IsActive: boolean;
}

export interface ConversationReply {
  ConversationId: number;
  ReplyId: number;
  Body: string;
  IsFavorite: boolean;
  IsEnable: boolean;
  FavoriteCount: number;
  CreatedById: number;
  CreatedBy: string;
  UpdatedBy: string;
  CreatedAt: string;
  UpdatedAt: string;
  HasBeenEdited: boolean;
  SideMenuText: string | null;
  personeId: number;
  LinkedReplyID: number | null;
  reactions: any[];
  LinkedReply: any;
  OriginalConversationId: number;
  Conversation: any;
  LinkedItem: any;
  ReplyLayers: ReplyLayer[];
  Attachments: any[];
  EmbeddedImages: any;
  Tags: any;
  possibleTags: any;
  Hashtags: any;
  CoreTaskID: number | null;
  CoreTasks: any;
  ProjectID: number | null;
  CreatedByIDConversation: number;
  TitleConversation: string | null;
  LastUpdatedAt: string | null;
  IsProjectConversation: boolean;
  ImageName: string;
  TenantId: number;
  IsDetailUpdate: boolean;
  SocketConnectionId: string | null;
  ConversationPhaseId: number;
  PhaseName: string;
  IsImportant: boolean;
  SubmitReplyWarningId: number | null;
}

export interface ReplyLayer {
  ReplyLayerId: number;
  ReplyId: number;
  LayerId: number;
  LayerName: string;
  LayerColor: string;
  PriorityInConfigValue: number;
}

export interface ConversationPhase {
  ConversationPhaseId: number;
  ConversationId: number;
  Name: string;
  Order: number;
  IsActive: boolean;
}
